// TODO (Noah, 2024-10-09): Re-enable this rule
/* eslint-disable replo/consistent-component-exports */
import type { ReploElementMetadata } from "schemas/generated/element";
import type { ReploProject } from "schemas/generated/project";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { FullScreenLoader } from "@v2/components/FullScreenLoader";
import { ReploAgentProvider } from "@v2/contexts/ReploAgentContext";
import WorkspaceProvider from "@v2/contexts/WorkspaceProvider";
import AgentConnectionDebugPanel from "@v2/features/agent-debugger/AgentDebugPanel";
import { initProjectBasedAnalytics } from "@v2/features/infra/analytics/init";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useDynamicCommandMenuItem } from "@v2/hooks/useDynamicCommandMenuItems";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { useLocalStorage } from "@v2/hooks/useLocalStorage";
import { elementRouter } from "@v2/trpc/routes/element";
import { projectRouter } from "@v2/trpc/routes/project";
import { subscriptionRouter } from "@v2/trpc/routes/subscription";
import { routes } from "@v2/utils/router";
import { getTRPCErrorStatus } from "@v2/utils/trpcErrorUtils";
import { Copy, ExternalLink } from "lucide-react";
import { Navigate } from "react-router-dom";

type CurrentProjectContext = {
  project?: ReploProject;
  isLoading: boolean;
  // TODO (Fran 2024-08-29): improve this type
  error: any;
  userRole?:
    | "superuser"
    | "workspaceOwner"
    | "workspaceMember"
    | "projectMember";
  projectId: string | undefined;
  elementMetadata?: ReploElementMetadata[];
  isElementMetadataSuccess: boolean;
};

const CurrentProjectContext = React.createContext<CurrentProjectContext>({
  project: undefined,
  isLoading: false,
  error: undefined,
  userRole: undefined,
  projectId: undefined,
  elementMetadata: undefined,
  isElementMetadataSuccess: false,
});

const DebugCommandMenuProjectItemsProvider = () => {
  const isDebugMode = useIsDebugMode();
  const projectContext = useCurrentProjectContext();
  // Open Shop in Admin
  const openShopInAdminItem = React.useMemo(() => {
    if (!isDebugMode || !projectContext?.project) {
      return null;
    }

    return {
      label: "Open Shop in Admin",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: ExternalLink },
      onSelect: () => {
        window.open(
          `https://admin.replo.app/dashboard/shops?projectId=${projectContext?.project?.id}`,
          "_blank",
        );
      },
    };
  }, [isDebugMode, projectContext?.project]);
  useDynamicCommandMenuItem("debug-open-shop-admin", openShopInAdminItem);

  // Copy Shop ID
  const copyShopIdItem = React.useMemo(() => {
    if (!isDebugMode || !projectContext?.project) {
      return null;
    }

    return {
      label: "Copy Shop ID",
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: Copy },
      onSelect: () => {
        void navigator.clipboard.writeText(projectContext.project!.id);
      },
    };
  }, [isDebugMode, projectContext?.project]);
  useDynamicCommandMenuItem("debug-copy-shop-id", copyShopIdItem);

  return null;
};

export const useCurrentProjectContext = () => {
  return React.useContext(CurrentProjectContext);
};

export const useIsAgentEnabled = () => {
  const { project } = useCurrentProjectContext();
  return Boolean(project?.agentEnabledAt);
};

const CurrentProjectProvider: React.FC<
  React.PropsWithChildren<{
    projectId: string;
    saveProjectIdAsLastVisited: boolean;
  }>
> = ({ children, projectId, saveProjectIdAsLastVisited }) => {
  const {
    project,
    error,
    isLoading,
    userRole,
    agentToken,
    elementMetadata,
    isElementMetadataSuccess,
  } = useGetProjectQuery(projectId ?? null);

  const status = getTRPCErrorStatus(error);
  const isNoAccessError = status === 403;

  const localStorage = useLocalStorage();
  React.useEffect(() => {
    if (!saveProjectIdAsLastVisited || !project) {
      return;
    }
    localStorage.setItem("replo.project.lastVisitedProjectId", projectId);
  }, [projectId, saveProjectIdAsLastVisited, project, localStorage]);

  // Initialize project-based analytics
  const { user } = useCurrentUser();
  const subscriptionQueryArg =
    isNoAccessError || !project?.ownerWorkspaceId
      ? skipToken
      : { workspaceId: project.ownerWorkspaceId, projectId: project.id };

  const { data: subscriptionDetails } =
    subscriptionRouter.getActiveSubscription.useQuery(subscriptionQueryArg);

  React.useEffect(() => {
    if (project && user) {
      initProjectBasedAnalytics(project, subscriptionDetails ?? undefined);
    }
  }, [project, user, subscriptionDetails]);

  // Note (Noah, 2026-01-14): Redirect to the no-access error page instead of showing
  // an inline error screen
  if (isNoAccessError) {
    return <Navigate to={routes.error.noAccess} replace />;
  }

  if (status === 404 || (error && status !== 403)) {
    return <Navigate to={routes.home.root} replace />;
  }

  if (isLoading) {
    return <FullScreenLoader />;
  }

  return (
    <WorkspaceProvider
      workspaceId={project?.ownerWorkspaceId}
      saveWorkspaceIdAsLastVisited
    >
      <CurrentProjectContext.Provider
        value={{
          project,
          error,
          isLoading,
          userRole,
          projectId,
          elementMetadata,
          isElementMetadataSuccess,
        }}
      >
        <MaybeAgentProvider projectId={projectId} agentToken={agentToken}>
          {children}
          <DebugCommandMenuProjectItemsProvider />
        </MaybeAgentProvider>
      </CurrentProjectContext.Provider>
    </WorkspaceProvider>
  );
};

const MaybeAgentProvider: React.FC<
  React.PropsWithChildren<{
    projectId: string;
    agentToken: string | null;
  }>
> = ({ children, projectId, agentToken }) => {
  if (agentToken) {
    return (
      <ReploAgentProvider
        key={projectId}
        projectId={projectId}
        agentToken={agentToken}
      >
        {children}
        <AgentConnectionDebugPanel />
      </ReploAgentProvider>
    );
  }

  return <>{children}</>;
};

export default CurrentProjectProvider;

function useGetProjectQuery(projectId: string | null) {
  // Note (Ben O. 2025-02-25): We use refetchOnMount: "always" because when a users goes from project 1 -> dashboard -> project 1,
  // the projectId does not change so we do not invalidate these queries but the projectData is stale.
  const {
    data,
    isLoading: isProjectLoading,
    error,
    refetch,
  } = projectRouter.getV2.useQuery(projectId ?? skipToken, {
    refetchOnMount: "always",
  });

  const projectData = data?.project;
  const userRole = data?.userRole;
  const agentToken = data?.agentToken ?? null;

  const {
    data: elementMetadata,
    isLoading: isElementMetadataLoading,
    isSuccess: isElementMetadataSuccess,
  } = elementRouter.listAllElementsMetadata.useQuery(
    projectId ? { projectId } : skipToken,
    {
      refetchOnMount: "always",
    },
  );

  const isLoading = isProjectLoading || isElementMetadataLoading;

  React.useEffect(() => {
    if (projectData?.hostingType) {
      window.Pylon?.("setNewIssueCustomFields", {
        project_hosting_type: projectData.hostingType,
      });
    }
  }, [projectData?.hostingType]);

  React.useEffect(() => {
    if ((error as any)?.error?.status === 401) {
      window.location.href = "/auth/login";
    }
  }, [error]);

  return {
    project: projectData,
    refetch,
    isLoading,
    error,
    userRole,
    agentToken,
    elementMetadata,
    isElementMetadataSuccess,
  };
}
